import {
  isSupabaseConfigured,
  redirectToLoginOnSessionExpired,
} from "@/lib/supabase";
import { resolveAccessToken } from "@/lib/authToken";
import { extractOpenRouterErrorMessage } from "@/lib/openRouterErrors";
import type {
  ChatCompletionsRequest,
  EmbeddingsRequest,
  EmbeddingBatchResponse,
  EmbeddingSingleResponse,
  OpenRouterModel,
} from "@/types/aiProxy";

const SUPABASE_PROJECT_URL = import.meta.env.VITE_SUPABASE_URL?.trim() || "https://tsnbktwryvnbmgsvzxdt.supabase.co";
const AI_PROXY_BASE = `${SUPABASE_PROJECT_URL}/functions/v1/ai-proxy`;

function buildUrl(route: "chat/completions" | "embeddings" | "models"): string {
  return `${AI_PROXY_BASE}/${route}`;
}

async function getAuthHeaders(contentType = true): Promise<HeadersInit> {
  const accessToken = await resolveAccessToken();
  const headers: Record<string, string> = {
    Authorization: `Bearer ${accessToken}`,
  };
  if (contentType) headers["Content-Type"] = "application/json";
  return headers;
}

async function assertProxyOk(response: Response): Promise<void> {
  if (response.ok) return;
  if (response.status === 401) {
    await redirectToLoginOnSessionExpired();
  }
  throw await parseError(response);
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function extractErrorMessage(payload: unknown): string | null {
  const openRouterMsg = extractOpenRouterErrorMessage(payload);
  if (openRouterMsg) return openRouterMsg;

  if (typeof payload === "string") {
    const trimmed = payload.trim();
    return trimmed.length ? trimmed : null;
  }
  if (!payload || typeof payload !== "object") return null;

  const record = payload as Record<string, unknown>;
  const candidates: unknown[] = [
    (record.error as Record<string, unknown> | undefined)?.message,
    record.message,
    (record.error as Record<string, unknown> | undefined)?.details,
    record.details,
    (record.error as Record<string, unknown> | undefined)?.error,
    record.error,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim().length) {
      return candidate.trim();
    }
    if (candidate && typeof candidate === "object") {
      const serialized = safeStringify(candidate);
      if (serialized && serialized !== "{}") return serialized;
    }
  }
  return null;
}

export class AIProxyHttpError extends Error {
  status: number;
  statusText: string;
  url: string;
  headers: Record<string, string>;
  bodyText: string;
  bodyJson?: unknown;

  constructor(args: {
    message: string;
    status: number;
    statusText: string;
    url: string;
    headers: Record<string, string>;
    bodyText: string;
    bodyJson?: unknown;
  }) {
    super(args.message);
    this.name = "AIProxyHttpError";
    this.status = args.status;
    this.statusText = args.statusText;
    this.url = args.url;
    this.headers = args.headers;
    this.bodyText = args.bodyText;
    this.bodyJson = args.bodyJson;
  }
}

/**
 * OpenRouter/OpenAI embeddings return `{ data: [{ index, embedding }] }`.
 * Some callers return our compact `{ embedding }` / `{ embeddings }` shape (e.g. legacy routes).
 */
function normalizeEmbeddingsResponse(
  raw: unknown,
  inputIsArray: boolean
): EmbeddingSingleResponse | EmbeddingBatchResponse {
  if (!raw || typeof raw !== "object") {
    throw new Error("Invalid embedding response: expected JSON object");
  }

  const obj = raw as Record<string, unknown>;

  if (Array.isArray(obj.embedding)) {
    return {
      embedding: obj.embedding as number[],
      model: typeof obj.model === "string" ? obj.model : undefined,
      usage: obj.usage as EmbeddingSingleResponse["usage"],
    };
  }

  if (Array.isArray(obj.embeddings)) {
    return {
      embeddings: obj.embeddings as number[][],
      model: typeof obj.model === "string" ? obj.model : undefined,
      usage: obj.usage as EmbeddingBatchResponse["usage"],
    };
  }

  const data = obj.data;
  if (!Array.isArray(data) || data.length === 0) {
    throw new Error("Invalid embedding response format (missing embedding array)");
  }

  const rows = data as Array<{ index?: number; embedding?: unknown }>;
  const sorted = [...rows].sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
  const vectors: number[][] = [];
  for (const row of sorted) {
    if (!Array.isArray(row.embedding)) {
      throw new Error("Invalid embedding response format (missing embedding array)");
    }
    vectors.push(row.embedding as number[]);
  }

  const model = typeof obj.model === "string" ? obj.model : undefined;
  const usage = obj.usage as EmbeddingSingleResponse["usage"];

  if (inputIsArray) {
    return { embeddings: vectors, model, usage };
  }

  const first = vectors[0];
  if (!first) {
    throw new Error("Invalid embedding response format (missing embedding array)");
  }
  return { embedding: first, model, usage };
}

async function parseError(response: Response): Promise<AIProxyHttpError> {
  const text = await response.text();
  const statusFallback = `${response.status} ${response.statusText}`.trim();
  const headers = Object.fromEntries(response.headers.entries());

  try {
    const parsed = JSON.parse(text);
    const extracted = extractErrorMessage(parsed);
    return new AIProxyHttpError({
      message: extracted || text || statusFallback,
      status: response.status,
      statusText: response.statusText,
      url: response.url,
      headers,
      bodyText: text,
      bodyJson: parsed,
    });
  } catch {
    return new AIProxyHttpError({
      message: text || statusFallback,
      status: response.status,
      statusText: response.statusText,
      url: response.url,
      headers,
      bodyText: text,
    });
  }
}

export const aiProxyClient = {
  async getModels(): Promise<OpenRouterModel[]> {
    const response = await fetch(buildUrl("models"), {
      method: "GET",
      headers: await getAuthHeaders(false),
    });

    await assertProxyOk(response);

    const json = await response.json();
    return Array.isArray(json?.data) ? json.data : [];
  },

  async chatCompletions(
    payload: ChatCompletionsRequest,
    options: { stream?: boolean; signal?: AbortSignal; app?: string } = {}
  ): Promise<Response> {
    const stream = options.stream ?? payload.stream ?? false;
    const url = buildUrl("chat/completions");
    const headers = await getAuthHeaders(true);
    const app = options.app?.trim();
    if (app) (headers as Record<string, string>)["x-app"] = app;
    const body = JSON.stringify({ ...payload, stream });

    console.log(`\n[AI PROXY CLIENT] ===== CHAT COMPLETIONS API CALL =====`);
    console.log(`[AI PROXY CLIENT] Timestamp:`, new Date().toISOString());
    console.log(`[AI PROXY CLIENT] Endpoint URL:`, url);
    console.log(`[AI PROXY CLIENT] Method: POST`);
    console.log(`[AI PROXY CLIENT] Request Headers:`, headers);
    console.log(`[AI PROXY CLIENT] Request Body:`, JSON.parse(body));
    console.log(`[AI PROXY CLIENT] Model:`, payload.model);
    console.log(`[AI PROXY CLIENT] Stream:`, stream);
    console.log(`[AI PROXY CLIENT] Messages Count:`, payload.messages?.length || 0);

    let response: Response;
    try {
      console.log(`[AI PROXY CLIENT] Sending request...`);
      const startTime = Date.now();

      response = await fetch(url, {
        method: "POST",
        headers,
        body,
        signal: options.signal,
      });

      const elapsed = Date.now() - startTime;
      console.log(`[AI PROXY CLIENT] Response received in ${elapsed}ms`);
      console.log(`[AI PROXY CLIENT] Response Status:`, response.status, response.statusText);
      console.log(`[AI PROXY CLIENT] Response Headers:`, {
        contentType: response.headers.get("content-type"),
        contentLength: response.headers.get("content-length"),
      });

      await assertProxyOk(response);
      console.log(`[AI PROXY CLIENT] Response validation: OK`);

      return response;
    } catch (error) {
      console.error(`[AI PROXY CLIENT] ❌ ERROR DURING API CALL:`, {
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        timestamp: new Date().toISOString(),
      });
      throw error;
    }
  },

  async embeddings(_payload: EmbeddingsRequest): Promise<EmbeddingSingleResponse | EmbeddingBatchResponse> {
    throw new Error(
      "Embeddings via OpenRouter/ai-proxy are disabled. Use embeddingService (/api/embeddings, Featherless) instead."
    );
  },
};
